import { Button, ConfigProvider, Flex, Form, Modal, Select, Typography } from "antd";
import type { FormInstance } from "antd";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { RequiredChoiceButton } from "components/modals/confirmModal";
import { buildCancelTypeOptions } from "components/bookings/cancelTypeOptions";
import { translate, translateWithVars } from "shared/languages";
import { textPx } from "shared/constants/zoom";

/**
 * The Selesaikan Booking / Pembatalan Booking confirms (prototype chrome),
 * shared by the per-row actions (ScheduleBooking) and the bulk actions
 * (BatchBookingActions) so the modal styling lives in one place. Presentation
 * only — the callers keep their own mutations and refund/non-refund mapping.
 *
 * Button colors follow the popup system: navy confirm (Selesaikan), solid-ink
 * dismiss (Batal), red destructive (Batalkan Booking).
 */

// Shared pill-button footer wrapped in the popup button-color theme.
const confirmFooter = (
  confirmLabel: string,
  onConfirm: () => void,
  onDismiss: () => void,
  destructive: boolean
) => (
  <ConfigProvider theme={modalButtonsTheme}>
    <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
      <Button onClick={onDismiss}>{translate("button.cancel")}</Button>
      <Button
        type="primary"
        danger={destructive}
        onClick={() => {
          onConfirm();
          Modal.destroyAll();
        }}
      >
        {confirmLabel}
      </Button>
    </Flex>
  </ConfigProvider>
);

// Green-dot confirm: credits will be deducted. `messageSuffix` lets the bulk
// flow append its "(n bookings)" count.
export const showCompleteBookingConfirm = ({
  messageSuffix = "",
  onConfirm,
}: {
  messageSuffix?: string;
  onConfirm: () => void;
}) => {
  Modal.success({
    icon: null,
    width: 380,
    title: (
      <ModalHeader
        tone="success"
        title={translate("messages.warning.complete_booking.title")}
      />
    ),
    content: (
      <Typography.Text style={modalSubtitleStyle}>
        {translate("messages.warning.complete_booking.content")}
        {messageSuffix}
      </Typography.Text>
    ),
    closable: true,
    maskClosable: true,
    footer: confirmFooter(
      translate("button.complete"),
      onConfirm,
      () => Modal.destroyAll(),
      false
    ),
    onCancel() {
      Modal.destroyAll();
    },
  });
};

// Amber-dot destructive confirm with the cancellation-type select. Options are
// the shared Batalkan / No Show pair (cancelTypeOptions) — the same labels as
// the per-row cancel modal, so the two can't drift (the old "Refund" /
// "Non-refund" labels also collided with the Refunded booking status). The
// caller owns the form instance and receives the chosen `isRefund` value
// (Batalkan maps to true, No Show to false). The confirm button stays
// disabled until a type is chosen: an unset choice used to be sent as No show,
// charging every selected client a credit. The line under the select says how
// many bookings are selected and that each can still be undone on its row
// (it used to call the cancel irreversible, System Review B22).
export const showCancelBookingConfirm = ({
  form,
  count,
  onConfirm,
}: {
  form: FormInstance;
  count: number;
  onConfirm: (isRefund: boolean) => void;
}) => {
  const dismiss = () => {
    Modal.destroyAll();
    form.resetFields();
  };
  Modal.warning({
    icon: null,
    width: 380,
    title: (
      <ModalHeader
        tone="amber"
        title={translate("messages.warning.cancel_booking.title")}
      />
    ),
    content: (
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="isRefund"
          required={true}
          label={translate("label.cancel_type")}
        >
          <Select
            placeholder={`${translate("form.placeholders.select")} ${translate(
              "label.cancel_type"
            )}`}
            options={buildCancelTypeOptions({ cancelled: true, noShow: false })}
          />
        </Form.Item>
        <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
          {count === 1
            ? translate("messages.warning.cancel_booking.content_bulk_one")
            : translateWithVars("messages.warning.cancel_booking.content_bulk", {
                n: count,
              })}
        </Typography.Text>
      </Form>
    ),
    closable: true,
    maskClosable: true,
    footer: (
      <ConfigProvider theme={modalButtonsTheme}>
        <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
          <Button onClick={dismiss}>{translate("button.cancel")}</Button>
          <RequiredChoiceButton
            choiceForm={form}
            choiceName="isRefund"
            type="primary"
            danger
            onClick={() => {
              const isRefund = form.getFieldValue("isRefund");
              if (typeof isRefund === "boolean") onConfirm(isRefund);
              Modal.destroyAll();
              form.resetFields();
            }}
          >
            {translate("button.cancel_booking")}
          </RequiredChoiceButton>
        </Flex>
      </ConfigProvider>
    ),
    onCancel: dismiss,
  });
};
